import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { Card, CardContent, CircularProgress, List, ListItem } from '@mui/material';
import { AppFormLayout, AppTextStyle, ImplicitSubmit } from '@shared';

import AppFormProvider from '@shared/AppFormProvider';
import FormPageActions from '@shared/FormPageActions';

import BasicTextField from '@shared/BasicTextField';

import { ta } from 'utils';
import axios from 'utils/axios-rails';

import debounce from 'lodash/debounce';

const propTypes = {
  submitURL: PropTypes.string.isRequired,
  deleteURL: PropTypes.string,
  record: PropTypes.object.isRequired,
  update: PropTypes.bool,

  processTemplateURL: PropTypes.string.isRequired, // URL to process the template on backend
  processedTemplate: PropTypes.string, // Initial value for the processed template when editing existing record
}

const defaultProps = {
  update: false,
}

const MessageTemplateForm = (props) => {
  const [processingLoading, setProcessingLoading] = useState(false); // Loader for processed template
  const [processedTemplate, setProcessedTemplate] = useState(props.processedTemplate); // To preview the template with WhatsApp


  // Process template on backend each x seconds to build the preview link
  const debouncedProcessTemplate = useCallback( debounce( async (template) => {
    if(!template){
      setProcessedTemplate(null);
    }
    else{
      try {
        const response = await axios.post(props.processTemplateURL, { template })
        setProcessedTemplate(response.data.payload);
      } catch (error) {
        alert(I18n.t("messages.request_error"));
      }
    }  
    setProcessingLoading(false);  
  }, 5000), []);

  // Process template to display preview
  const handleTemplateChange = (newValue, onChangeCallback) => {
    setProcessingLoading(true);
    onChangeCallback("template", newValue);
    debouncedProcessTemplate(newValue);
  }


  return (
    <AppFormProvider
      title={I18n.t("activerecord.models.message_template.one")}
      defaultValues={ props.record }
      submitURL={ props.submitURL }
      submitMethod={ props.update ? "PUT" : "POST" }
      paramsName="message_template"
      pageProps={props.page}
    >
      {(renderProps) =>
        <Card>
          <CardContent>
          <form onSubmit={renderProps.handleSubmit}>
            <AppFormLayout>
              <BasicTextField
                name="title"
                label={ta("general.title")}
                placeholder="Template de entrega"
                {...renderProps}
              />
              <BasicTextField
                name="template"
                label={ta("message_template.template")}
                multiline={3}
                {...renderProps}
                onChange={ (value) => handleTemplateChange(value, renderProps.setFieldValue) }
              />

              {/* Template system hints */}
              <AppTextStyle variation="subdued">
                Para introducir valores o elementos se debe seguir el siguiente formato: [ITEM], ejemplo: [courier_name].
                <br />
                Variables disponibles:
                <List dense sx={{ listStyleType: 'disc', pl: 3 }}>
                  { [
                    'customer_name: Nombre del cliente',
                    'courier_name: Nombre del repartidor',
                    'order_number: Número de orden',
                    'street_address: Dirección de entrega',
                    'edit_address_url: Link para editar la dirección de entrega',
                    'br: Cambio de línea',
                  ].map((variable) => (
                    <ListItem key={variable} disablePadding sx={{ display: 'list-item' }}>
                      { variable }
                    </ListItem>
                  ))}
                </List>
              </AppTextStyle>

              <hr />

              {/* WhatsApp formatting hints */}
              <AppTextStyle variation="subdued">
                Adicionalmente se puede utilizar formato propio de "WhatsApp" para agregar estilo a los textos, ejemplo: Para agregar bold <strong>"*Texto en Bold*"</strong> .
                <br/>
                Para más información: <a target='_blank' href="https://faq.whatsapp.com/539178204879377">https://faq.whatsapp.com/539178204879377</a>
              </AppTextStyle>
            </AppFormLayout>

            { !!processedTemplate &&
              <div className='mt12'>
                <div className="flex align-items-center">
                  <a className='mr8' target='_blank' href={`https://wa.me/?text=${processedTemplate}`}>Previsualizar con datos</a>

                  { processingLoading && <CircularProgress size={16} aria-label="Loading" /> }
                </div>
                <p>
                  <AppTextStyle variation="subdued">
                    Si abre link desde teléfono puede previsualizar como se ve en WhatsApp.
                  </AppTextStyle>
                </p>
              </div>
            }

            <div className='mt12'>
              <FormPageActions
                onPrimaryAction={renderProps.handleSubmit}
                deleteURL={ props.deleteURL }
              />
            </div>
            <ImplicitSubmit />
          </form>
          </CardContent>
        </Card>
      }
    </AppFormProvider>    
  )
};

MessageTemplateForm.propTypes = propTypes;
MessageTemplateForm.defaultProps = defaultProps;

export default MessageTemplateForm;